import { useLayoutEffect, useMemo } from 'react';
import { SiteTheme, UISite } from '@/services/ui-customization.service';

type RGB = [number, number, number];

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

// How far each shade moves from the brand color: + toward white, - toward black.
// 400-600 are all the exact brand color, so every solid button, banner and bar looks the same
// whichever shade a page used; 700+ get darker for hover and dark text, 50-300 are light tints.
const SHADE_MIX: Record<(typeof SHADES)[number], number> = {
  50: 0.94, 100: 0.88, 200: 0.76, 300: 0.55, 400: 0, 500: 0,
  600: 0, 700: -0.12, 800: -0.25, 900: -0.4, 950: -0.6,
};

// Palettes rebuilt from the primary color: the theme's primary plus the brand roles
// (brand-blue, brand-indigo, ...) that buttons, tabs, links and progress bars use
// Keep in sync with BRAND_FAMILIES in tailwind.config.ts
const BRAND_PALETTES = [
  'color-primary',
  'brand-blue',
  'brand-indigo',
  'brand-purple',
  'brand-violet',
  'brand-amber',
  'brand-orange',
  'brand-green',
  'brand-emerald',
  'brand-yellow',
  'brand-teal',
  'brand-cyan',
  'brand-pink',
] as const;

// Menu hover on a default white sidebar, per site (admin gray-50, learner gray-100)
const DEFAULT_SIDEBAR_HOVER: Record<UISite, RGB> = {
  admin: [249, 250, 251],
  learner: [243, 244, 246],
  auth: [243, 244, 246],
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const hexToRgb = (hex: string): RGB => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as RGB;

// Tailwind reads these variables as "R G B" so opacity modifiers like bg-primary-100/50 keep working
const toVar = (rgb: RGB) => rgb.map((c) => Math.round(c)).join(' ');

const mix = (rgb: RGB, amount: number): RGB => {
  const target = amount >= 0 ? 255 : 0;
  const weight = Math.abs(amount);
  return rgb.map((c) => c + (target - c) * weight) as RGB;
};

// WCAG relative luminance, used to keep text readable on a custom background
const luminance = ([r, g, b]: RGB) => {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const isDarkColor = (hex: string) => HEX_COLOR.test(hex) && luminance(hexToRgb(hex)) < 0.4;

export const contrastRatio = (hexA: string, hexB: string) => {
  const [l1, l2] = [luminance(hexToRgb(hexA)), luminance(hexToRgb(hexB))].sort((a, b) => b - a);
  return (l1 + 0.05) / (l2 + 0.05);
};

// Keep only valid values the tenant actually set; everything else keeps the default look
const pickSet = (theme?: SiteTheme): SiteTheme =>
  Object.fromEntries(
    Object.entries(theme || {}).filter(
      ([, v]) => typeof v === 'string' && v.trim() !== '' && HEX_COLOR.test(v)
    )
  ) as SiteTheme;

const ALL_VARS = [
  '--ui-bg',
  '--ui-sidebar',
  '--ui-sidebar-text',
  '--ui-sidebar-active',
  '--ui-sidebar-hover',
  ...BRAND_PALETTES.flatMap((palette) => SHADES.map((s) => `--${palette}-${s}`)),
];

// Set on <html> while a primary color is saved; index.css uses it to color native checkboxes, radios and sliders
const PRIMARY_ATTR = 'data-ui-primary';

const clearTheme = () => {
  const root = document.documentElement;
  ALL_VARS.forEach((name) => root.style.removeProperty(name));
  root.removeAttribute(PRIMARY_ATTR);
};

// Last theme seen per site, so a page load can paint with it before the API answers.
// localStorage is per origin, so each tenant domain keeps its own copy.
const cacheKey = (site: UISite) => `uiCustomization:${site}`;

const readCachedTheme = (site: UISite): SiteTheme | undefined => {
  try {
    const raw = localStorage.getItem(cacheKey(site));
    return raw ? (JSON.parse(raw) as SiteTheme) : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedTheme = (site: UISite, theme: SiteTheme) => {
  try {
    if (Object.keys(theme).length) localStorage.setItem(cacheKey(site), JSON.stringify(theme));
    else localStorage.removeItem(cacheKey(site));
  } catch {
    // Storage can be blocked (private mode); the theme then just appears once the API answers
  }
};

/**
 * Saved values for one site (only the fields the tenant set).
 * Components use it to decide whether to switch from their default classes to the theme roles.
 */
export function useSiteTheme(site: UISite) {
  const saved = useMemo(() => pickSet(readCachedTheme(site)), [site]);

  return {
    saved,
    hasCustomSidebar: Boolean(saved.sidebar_background || saved.sidebar_text),
    hasCustomSidebarActive: Boolean(saved.sidebar_active),
  };
}

/**
 * Applies the tenant's saved UI Customization for one site (admin or learner) as CSS variables on <html>.
 * Fields left empty keep the default Skillzen look; everything is removed again when the layout unmounts.
 */
export default function useApplyUITheme(site: UISite) {
  const { saved } = useSiteTheme(site);

  // Layout effect: runs before paint, so the first frame is already themed
  useLayoutEffect(() => {
    const root = document.documentElement;
    const set = (name: string, rgb: RGB) => root.style.setProperty(name, toVar(rgb));
    const unset = (name: string) => root.style.removeProperty(name);

    // Page background
    if (saved.page_background) set('--ui-bg', hexToRgb(saved.page_background));
    else unset('--ui-bg');

    

    // Brand: rebuild every brand palette from the primary color, otherwise keep the exact defaults
    if (saved.primary_color) root.setAttribute(PRIMARY_ATTR, '');
    else root.removeAttribute(PRIMARY_ATTR);

    if (saved.primary_color) {
      const base = hexToRgb(saved.primary_color);
      BRAND_PALETTES.forEach((palette) =>
        SHADES.forEach((shade) => set(`--${palette}-${shade}`, mix(base, SHADE_MIX[shade])))
      );
    } else {
      BRAND_PALETTES.forEach((palette) => SHADES.forEach((shade) => unset(`--${palette}-${shade}`)));
    }

    // Sidebar background and the hover shade derived from it
    if (saved.sidebar_background) {
      const bg = hexToRgb(saved.sidebar_background);
      set('--ui-sidebar', bg);
      set('--ui-sidebar-hover', isDarkColor(saved.sidebar_background) ? mix(bg, 0.12) : mix(bg, -0.05));
    } else {
      unset('--ui-sidebar');
      set('--ui-sidebar-hover', DEFAULT_SIDEBAR_HOVER[site]);
    }

    // Sidebar text: the saved color, or a readable one picked from a custom background
    if (saved.sidebar_text) set('--ui-sidebar-text', hexToRgb(saved.sidebar_text));
    else if (saved.sidebar_background)
      set('--ui-sidebar-text', isDarkColor(saved.sidebar_background) ? [229, 231, 235] : [75, 85, 99]);
    else unset('--ui-sidebar-text');

    // Selected sidebar item: its own color, else the primary color
    const active = saved.sidebar_active || saved.primary_color;
    if (active) set('--ui-sidebar-active', hexToRgb(active));
    else unset('--ui-sidebar-active');

    return clearTheme;
  }, [saved, site]);
}

export const saveCustomizationToStorage = (apiResponse: any) => {
  const payload = apiResponse?.data || apiResponse;

  if (payload?.admin) {
    writeCachedTheme('admin', pickSet(payload.admin));
  }
  if (payload?.learner) {
    writeCachedTheme('learner', pickSet(payload.learner));
  }
   if (payload?.auth) {
    writeCachedTheme('auth', pickSet(payload.auth));
  }
};